import type { HsNavigationMinScreenSize } from '@lib/core-ts/viewer/culling/navigation-culling.class';
import {
  HS_DEFAULT_RENDER_ORDER_STRATEGY,
  type HsRenderOrderStrategy,
} from '@lib/core-ts/viewer/streaming/stable-render-order.class';
import {
  hsDefaultTileCacheBudgetMiB,
  HS_DESKTOP_TILE_CACHE_BUDGET_MIB,
} from '@lib/shared/streaming/tile-cache-eviction.function';

interface ViewerRuntimeField {
  readonly key: string;
  readonly label: string;
  readonly description: string;
  readonly group: string;
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly unit?: ViewerRuntimeUnit;
}

export type ViewerRuntimeUnit = 'K' | 'M';

export type ViewerNavigationMinScreenSize = HsNavigationMinScreenSize;

export const DEFAULT_VIEWER_NAVIGATION_MIN_SCREEN_SIZE: ViewerNavigationMinScreenSize = 16;

export const VIEWER_NAVIGATION_MIN_SCREEN_SIZES: readonly {
  readonly value: ViewerNavigationMinScreenSize;
  readonly label: string;
}[] = [
  { value: 0, label: 'Off' },
  { value: 8, label: '8 px' },
  { value: 16, label: '16 px' },
  { value: 32, label: '32 px' },
];

const VIEWER_RUNTIME_UNIT_MULTIPLIERS: Record<ViewerRuntimeUnit, number> = {
  K: 1_000,
  M: 1_000_000,
};

export const VIEWER_RENDER_ORDER_STRATEGIES: readonly {
  readonly value: HsRenderOrderStrategy;
  readonly label: string;
  readonly description: string;
}[] = [
  {
    value: 'stable-geometry',
    label: 'Stable geometry priority',
    description: 'Prioritizes repeated and larger geometry while keeping the order stable.',
  },
  {
    value: 'largest-first',
    label: 'Largest objects first',
    description: 'Prioritizes objects with the largest world-space bounds.',
  },
  {
    value: 'smallest-first',
    label: 'Smallest objects first',
    description: 'Prioritizes small objects and details before larger objects.',
  },
  {
    value: 'source-order',
    label: 'Source order',
    description: 'Uses deterministic geometry and node keys without size-based prioritization.',
  },
];

export type ViewerRenderOrderStrategy = (typeof VIEWER_RENDER_ORDER_STRATEGIES)[number]['value'];

export const DEFAULT_VIEWER_RENDER_ORDER_STRATEGY: ViewerRenderOrderStrategy = HS_DEFAULT_RENDER_ORDER_STRATEGY;

export const VIEWER_GEOMETRY_FORMATS: readonly {
  readonly value: 'binary' | 'draco';
  readonly label: string;
  readonly description: string;
}[] = [
  {
    value: 'binary',
    label: 'Binary',
    description: 'Current lossless binary geometry format.',
  },
  {
    value: 'draco',
    label: 'Draco',
    description: 'Draco-compressed geometry with a smaller payload and slower decoding.',
  },
];

export type ViewerGeometryFormat = (typeof VIEWER_GEOMETRY_FORMATS)[number]['value'];

export const VIEWER_POINT_CLOUD_GEOMETRY_FORMATS: readonly {
  readonly value: 'binary' | 'draco' | 'quantized' | 'quantized2';
  readonly label: string;
  readonly description: string;
}[] = [
  ...VIEWER_GEOMETRY_FORMATS,
  {
    value: 'quantized',
    label: 'Quantized',
    description:
      '16-bit positions inside each block and RGB colors: about 9 bytes per point instead of 17, decoded without Draco.',
  },
  {
    value: 'quantized2',
    label: 'Quantized, adaptive precision',
    description:
      'Positions with as many bits as each block needs (4 bytes per point) and RGB565 colors: about 6 bytes per point.',
  },
];

export type ViewerPointCloudGeometryFormat = (typeof VIEWER_POINT_CLOUD_GEOMETRY_FORMATS)[number]['value'];

export const DEFAULT_VIEWER_GEOMETRY_FORMAT: ViewerGeometryFormat = 'binary';
export const DEFAULT_POINT_CLOUD_GEOMETRY_FORMAT: ViewerPointCloudGeometryFormat = 'quantized2';

export const VIEWER_FRUSTUM_RESPONSE_FORMATS: readonly {
  readonly value: 'binary' | 'json';
  readonly label: string;
  readonly description: string;
}[] = [
  {
    value: 'binary',
    label: 'Binary',
    description: 'Compact lossless binary response. This is the default and has the smallest payload.',
  },
  {
    value: 'json',
    label: 'JSON',
    description: 'Compact JSON response for inspection and compatibility; it has a larger payload than binary.',
  },
];

export type ViewerFrustumResponseFormat = (typeof VIEWER_FRUSTUM_RESPONSE_FORMATS)[number]['value'];

export const DEFAULT_VIEWER_FRUSTUM_RESPONSE_FORMAT: ViewerFrustumResponseFormat = 'binary';

export const VIEWER_FRUSTUM_SORT_ORDERS: readonly {
  readonly value: 'distance' | 'angular-size';
  readonly label: string;
  readonly description: string;
}[] = [
  {
    value: 'distance',
    label: 'Distance',
    description: 'Nearest AABB first.',
  },
  {
    value: 'angular-size',
    label: 'Angular size',
    description: 'Most visible objects first by angular size, then nearest AABB.',
  },
];

export type ViewerFrustumSortOrder = (typeof VIEWER_FRUSTUM_SORT_ORDERS)[number]['value'];

export const DEFAULT_VIEWER_FRUSTUM_SORT_ORDER: ViewerFrustumSortOrder = 'angular-size';

export const VIEWER_RUNTIME_FIELDS: readonly ViewerRuntimeField[] = [
  {
    key: 'downloadBatchSize',
    label: 'Download min batch (geometries)',
    description:
      'Minimum unique geometries per request. In manual mode this is the exact batch size; at most 10000 geometries per request.',
    group: 'Render pipeline',
    value: 500,
    min: 1,
    max: 10000,
    step: 1,
  },
  {
    key: 'texturedMeshDownloadBatchSize',
    label: 'Textured asset download batch',
    description:
      'Maximum textured mesh geometries per request. Texture downloads also use this setting, up to the API limit of 100 documents per request.',
    group: 'Render pipeline',
    value: 50,
    min: 1,
    max: 10000,
    step: 1,
  },
  {
    key: 'downloadMinBatchBytes',
    label: 'Download min batch size (MB)',
    description:
      'Mesh auto mode: target mesh response size in MB, estimated from completed mesh batches. Download min batch is the count floor, with 10000 geometries as the ceiling. Lines and point clouds use their own fixed batch settings.',
    group: 'Render pipeline',
    value: 5500000,
    min: 500000,
    max: 20000000,
    step: 500000,
    unit: 'M',
  },
  {
    key: 'pointCloudDownloadBatchSize',
    label: 'Point cloud download batch (geometries)',
    description: 'Point-cloud geometry blocks requested per binary download. Smaller batches render sooner.',
    group: 'Render pipeline',
    value: 100,
    min: 1,
    max: 2000,
    step: 1,
  },
  {
    key: 'frameBudgetMs',
    label: 'Frame draw budget (ms)',
    description:
      'Draw time per frame in distributed mode. Lower values improve responsiveness but take more frames to complete the image.',
    group: 'Render pipeline',
    value: 32,
    min: 1,
    max: 50,
    step: 1,
  },
  {
    key: 'drawCountInertiaFrames',
    label: 'Draw count inertia (frames)',
    description:
      'Repeats the measured objects-per-frame count for the next 2 to 50 frames after a render restart; this reduces pacing jumps during camera movement.',
    group: 'Render pipeline',
    value: 20,
    min: 2,
    max: 50,
    step: 1,
  },
  {
    key: 'renderBatchSize',
    label: 'Geometry batch (objects)',
    description:
      'Objects added per geometry batch. Smaller batches reduce pauses; the work budget is checked between batches.',
    group: 'Render pipeline',
    value: 500,
    min: 1,
    max: 2000,
    step: 1,
  },
  {
    key: 'renderBudgetMs',
    label: 'Geometry work budget (ms)',
    description:
      'Geometry preparation time before yielding to the browser, checked between batches. Lower values reduce loading pauses.',
    group: 'Render pipeline',
    value: 40,
    min: 1,
    max: 50,
    step: 1,
  },
  {
    key: 'pointCloudPointSize',
    label: 'Point cloud point size (px)',
    description: 'Base screen size of rendered point-cloud points. Larger values make sparse clouds easier to see.',
    group: 'Render pipeline',
    value: 3,
    min: 1,
    max: 12,
    step: 0.5,
  },
  {
    key: 'meshLimit',
    label: 'Mesh limit (K)',
    description:
      'Maximum mesh objects retained by the server frustum session, not triangles. Enter values in thousands (K); lower values reduce scene load.',
    group: 'Frustum',
    value: 150000,
    min: 1000,
    max: 1000000,
    step: 1000,
    unit: 'K',
  },
  {
    key: 'lineLimit',
    label: 'Line limit (K)',
    description:
      'Maximum line objects retained by the server frustum session, not individual segments or vertices. Enter values in thousands (K).',
    group: 'Frustum',
    value: 150000,
    min: 1000,
    max: 1000000,
    step: 1000,
    unit: 'K',
  },
  {
    key: 'pointCloudBudget',
    label: 'Point cloud budget (M)',
    description:
      'Point budget of the point budget LOD in millions (M). 0 picks it from the screen size and device: about eight points per pixel, 1 to 10 M on desktops, at most 3 M with 4 GB of device memory or less and 2 M on phones and tablets.',
    group: 'Frustum',
    value: 0,
    min: 0,
    max: 30000000,
    step: 100000,
    unit: 'M',
  },
  {
    key: 'pointCloudFrameBudgetMs',
    label: 'Point cloud frame budget (ms)',
    description:
      'GPU draw time per frame the point cloud draw budget aims below while the camera moves. Lower values keep navigation smoother on weak GPUs; higher values draw more points.',
    group: 'Frustum',
    value: 14,
    min: 4,
    max: 50,
    step: 1,
  },
  {
    key: 'meshAngle',
    label: 'Mesh min angle (deg)',
    description:
      'Minimum apparent mesh size in degrees. Lower values include smaller distant objects; higher values reduce detail and load.',
    group: 'Frustum',
    value: 0.5,
    min: 0,
    max: 180,
    step: 0.01,
  },
  {
    key: 'lineAngle',
    label: 'Line min angle (deg)',
    description: 'Minimum apparent line-object size in degrees. Lower values include smaller distant objects.',
    group: 'Frustum',
    value: 1,
    min: 0,
    max: 180,
    step: 0.1,
  },
  {
    key: 'farPlane',
    label: 'Far plane (K)',
    description:
      'Maximum camera draw distance in thousands (K). Objects beyond this distance are outside the camera frustum.',
    group: 'Camera',
    value: 20000,
    min: 1000,
    max: 1000000,
    step: 1000,
    unit: 'K',
  },
  {
    key: 'hlodMaxScreenErrorPixels',
    label: 'HLOD max screen error (px)',
    description:
      'HLOD tileset nodes are refined until their geometric error projects below this many pixels. Lower is sharper and loads more.',
    group: 'Frustum',
    value: 3,
    min: 0.5,
    max: 64,
    step: 0.5,
  },
  {
    key: 'photogrammetryCacheMiB',
    label: 'Photogrammetry tile cache (MiB)',
    description:
      'GPU and CPU memory streamed photogrammetry tiles may keep after they stop being drawn, so turning back does not download them again. Over the budget, tiles the camera would not draw from its position go first, then the least recently used; the coarsest level always stays. Defaults to 256 MiB on phones and tablets.',
    group: 'Frustum',
    value: HS_DESKTOP_TILE_CACHE_BUDGET_MIB,
    min: 64,
    max: 8192,
    step: 1,
  },
  {
    key: 'debounceMs',
    label: 'Request debounce (ms)',
    description:
      'Delay after the last camera change before requesting geometry and the minimum interval for adaptive scroll-speed updates. Higher values reduce requests; zero removes the delay.',
    group: 'Frustum',
    value: 220,
    min: 0,
    max: 5000,
    step: 1,
  },
] as const;

export type ViewerRuntimeSettings = Record<(typeof VIEWER_RUNTIME_FIELDS)[number]['key'], number>;

export function defaultViewerRuntimeSettings(): ViewerRuntimeSettings {
  const settings: ViewerRuntimeSettings = Object.fromEntries(
    VIEWER_RUNTIME_FIELDS.map((field: (typeof VIEWER_RUNTIME_FIELDS)[number]) => [field.key, field.value])
  ) as ViewerRuntimeSettings;
  settings.photogrammetryCacheMiB = hsDefaultTileCacheBudgetMiB();
  return settings;
}

export function getViewerRuntimeFieldMultiplier(field: (typeof VIEWER_RUNTIME_FIELDS)[number]): number {
  return field.unit === undefined ? 1 : VIEWER_RUNTIME_UNIT_MULTIPLIERS[field.unit];
}

export function formatViewerRuntimeFieldValue(field: (typeof VIEWER_RUNTIME_FIELDS)[number], value: number): number {
  return value / getViewerRuntimeFieldMultiplier(field);
}

export function parseViewerRuntimeFieldValue(
  field: (typeof VIEWER_RUNTIME_FIELDS)[number],
  rawValue: string,
  allowExplicitUnit: boolean = false
): number {
  const trimmedValue: string = rawValue.trim();
  if (trimmedValue.length === 0) {
    return NaN;
  }
  if (field.unit === undefined || !allowExplicitUnit) {
    const value: number = Number(trimmedValue);
    return field.unit === undefined ? value : value * getViewerRuntimeFieldMultiplier(field);
  }

  const match: RegExpExecArray | null = /^([+-]?(?:\d+(?:\.\d*)?|\.\d+))\s*([kKmM])?$/.exec(trimmedValue);
  if (match === null) {
    return NaN;
  }
  const unit: ViewerRuntimeUnit = (match[2]?.toUpperCase() as ViewerRuntimeUnit | undefined) ?? field.unit;
  return Number(match[1]) * VIEWER_RUNTIME_UNIT_MULTIPLIERS[unit];
}

export function formatViewerRuntimeUrlValue(field: (typeof VIEWER_RUNTIME_FIELDS)[number], value: number): string {
  if (field.unit === undefined) {
    return String(value);
  }
  return `${formatViewerRuntimeFieldValue(field, value)}${field.unit}`;
}

export function validateViewerRuntimeSettings(settings: ViewerRuntimeSettings): string | null {
  for (const field of VIEWER_RUNTIME_FIELDS) {
    const value: number = settings[field.key];
    if (
      !Number.isFinite(value) ||
      value < field.min ||
      value > field.max ||
      (field.step === 1 && !Number.isInteger(value)) ||
      (field.unit !== undefined && !Number.isInteger(value / field.step))
    ) {
      const unitSuffix: string = field.unit ?? '';
      const wholeNumberSuffix: string = field.unit !== undefined || field.step === 1 ? ' (whole number)' : '';
      return `${field.label}: enter ${formatViewerRuntimeFieldValue(
        field,
        field.min
      )}${unitSuffix} to ${formatViewerRuntimeFieldValue(field, field.max)}${unitSuffix}${wholeNumberSuffix}.`;
    }
  }
  return null;
}
